/**
 * Recovery for a tab that outlived a deploy.
 *
 * Every page is its own chunk with a hashed file name (routes/router.tsx), and
 * each deploy swaps in a fresh dist/ — the previous build's chunks are gone. A
 * tab opened before the deploy keeps running the old build, so the first time
 * it opens a page it has not loaded yet, the import 404s. Loading the current
 * build is the whole fix, and a reload does exactly that.
 */

/** How each browser reports a dynamic import whose file could not be fetched. */
const STALE_BUILD_MESSAGES = [
  "failed to fetch dynamically imported module", // Chrome, Edge
  "importing a module script failed", // Safari
  "error loading dynamically imported module", // Firefox
  "unable to preload css", // Vite, for a chunk's stylesheet
];

export function isStaleBuildError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return STALE_BUILD_MESSAGES.some((known) => message.includes(known));
}

const RELOADED_AT_KEY = "indira:stale-build-reloaded-at";

/** A second failure this soon after reloading means the live build is broken. */
const LOOP_GUARD_MS = 30_000;

let reloading = false;

/**
 * Reloads onto the current build. Returns false, and does nothing, when a
 * reload would not help or could loop:
 *
 * - the browser is offline: that is why the import failed, and a reload would
 *   swap the app for the browser's offline page;
 * - this tab already reloaded for the same reason within 30 s: the chunk is
 *   missing from the live build itself, and reloading again would never end;
 * - sessionStorage is unavailable, so that guard cannot be kept.
 */
export function reloadOntoCurrentBuild(): boolean {
  // Already on its way — a second boundary, or StrictMode's second effect run.
  if (reloading) return true;
  if (!navigator.onLine) return false;

  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT_KEY));
    if (Date.now() - last < LOOP_GUARD_MS) return false;
    sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  } catch {
    return false;
  }

  reloading = true;
  window.location.reload();
  return true;
}
